import React, { useState, useEffect, useCallback, useRef, TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import newsService from '../../../services/newsServices';
import { Article } from '../../../types/news.types';
import { getCategoryColor, getCategoryLabel } from '../../../utils/categories';
import { formatTimeAgo } from '../../../utils/dateFormatter';
import styles from './Hero.module.css';

const Hero: React.FC = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeTab, setActiveTab] = useState<'recent' | 'top'>('recent');
  const [isPaused, setIsPaused] = useState(false);
  const [heroSlides, setHeroSlides] = useState<Article[]>([]);
  const [recentNews, setRecentNews] = useState<Article[]>([]);
  const [topStories, setTopStories] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Fetch articles on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        console.log('🔄 Fetching hero section data...');
        setLoading(true);
        setError(null);

        // 1. Fetch "top" category articles for main slider (limit: 5)
        console.log('📡 Fetching TOP category articles...');
        const topCategoryResponse = await newsService.getArticlesByCategory('top', 1, 5);
        console.log('✅ Top category articles:', topCategoryResponse.articles);
        setHeroSlides(topCategoryResponse.articles);

        // 2. Fetch "startuprahi" category for Recent News tab (limit: 3)
        console.log('📡 Fetching STARTUPRAHI category articles...');
        const startuprahiResponse = await newsService.getArticlesByCategory('startuprahi', 1, 3);
        console.log('✅ Startuprahi articles:', startuprahiResponse.articles);
        setRecentNews(startuprahiResponse.articles);

        // 3. Fetch "stories" category for Top Stories tab (limit: 3)
        console.log('📡 Fetching STORIES category articles...');
        const storiesResponse = await newsService.getArticlesByCategory('stories', 1, 3);
        console.log('✅ Stories articles:', storiesResponse.articles);
        setTopStories(storiesResponse.articles);

        setLoading(false);
        console.log('✅ Hero section data loaded successfully');
      } catch (err: any) {
        console.error('❌ Failed to fetch articles:', err);
        setError(err.message || 'Failed to load news. Please try again later.');
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!isPaused && heroSlides.length > 0) {
      const interval = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, heroSlides.length]);

  const goToSlide = (index: number) => {
    setActiveSlide(index);
  };

  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = useCallback(() => {
    setActiveSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, [heroSlides.length]);

  // Touch/Swipe handlers
  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      nextSlide();
    }
    if (touchStartX.current - touchEndX.current < -50) {
      prevSlide();
    }
  };

  // Mouse drag handlers
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef<number>(0);
  const dragEndX = useRef<number>(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    dragEndX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragStartX.current - dragEndX.current > 50) {
      nextSlide();
    }
    if (dragStartX.current - dragEndX.current < -50) {
      prevSlide();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Get current tab articles
  const currentTabArticles = activeTab === 'recent' ? recentNews : topStories;

  // Loading state
  if (loading) {
    return (
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.topLabel}>
            <span className={styles.badge}>TOP</span>
            <h2 className={styles.sectionTitle}>Top News</h2>
          </div>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading top news...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.topLabel}>
            <span className={styles.badge}>TOP</span>
            <h2 className={styles.sectionTitle}>Top News</h2>
          </div>
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <button className={styles.retryButton} onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  // No articles state
  if (heroSlides.length === 0) {
    return (
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.topLabel}>
            <span className={styles.badge}>TOP</span>
            <h2 className={styles.sectionTitle}>Top News</h2>
          </div>
          <div className={styles.emptyState}>
            <p>No top news articles available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.heroSection}>
      <div className={styles.container}>
        {/* Top Label */}
        <div className={styles.topLabel}>
          <span className={styles.badge}>TOP</span>
          <h2 className={styles.sectionTitle}>Top News</h2>
        </div>

        <div className={styles.heroGrid}>
          {/* Main Hero Slider */}
          <div
            className={styles.heroSlider}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => {
              setIsPaused(false);
              handleMouseLeave();
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className={styles.slidesWrapper}>
              {heroSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`${styles.slide} ${index === activeSlide ? styles.active : ''}`}
                  style={{ backgroundImage: `url(${slide.featuredImage.url})` }}
                >
                  <div className={styles.slideOverlay} />
                  <div className={styles.slideContent}>
                    <div className={styles.slideTop}>
                      <span
                        className={styles.categoryBadge}
                        style={{ backgroundColor: getCategoryColor(slide.category) }}
                      >
                        {getCategoryLabel(slide.category)}
                      </span>
                      <span className={styles.author}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {slide.author.name}
                      </span>
                    </div>

                    <Link to={`/news/${slide.slug}`} className={styles.slideTitle}>
                      {slide.headline}
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Slide Indicators */}
            <div className={styles.indicators}>
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.indicator} ${index === activeSlide ? styles.activeIndicator : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Side News Section */}
          <div className={styles.sideNews}>
            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'recent' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('recent')}
              >
                Recent News
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'top' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('top')}
              >
                Top Story
              </button>
            </div>

            {/* News List */}
            <div className={styles.newsList}>
              {currentTabArticles.length > 0 ? (
                currentTabArticles.map((news, index) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.slug}`}
                    className={`${styles.newsCard} ${index === 1 ? styles.highlighted : ''}`}
                  >
                    <div className={styles.newsImage}>
                      <img
                        src={news.featuredImage.thumbnailUrl || news.featuredImage.url}
                        alt={news.featuredImage.alt || news.headline}
                        loading="lazy"
                      />
                    </div>
                    <div className={styles.newsContent}>
                      <div className={styles.newsTop}>
                        <span
                          className={styles.newsCategory}
                          style={{ color: getCategoryColor(news.category) }}
                        >
                          {getCategoryLabel(news.category)}
                        </span>
                        <span className={styles.timeAgo}>
                          {formatTimeAgo(news.publishedAt || news.createdAt)}
                        </span>
                      </div>
                      <h3 className={styles.newsTitle}>{news.headline}</h3>
                    </div>
                  </Link>
                ))
              ) : (
                <p className={styles.noArticles}>No articles available in this category.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;